import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Button,
  SelectChangeEvent,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import adminServices from "../../../services/adminServices";
import {
  getPolicyDashboardData,
  TablePolicyFormData,
} from "../../../types/adminSection";
import AddIcon from "../../../assets/images/AddIcon.svg";
import EditIcon from "../../../assets/images/editIcon.svg";
import DeleteIcon from "../../../assets/images/deleteIcon.svg";
import PolicyModal from "../../adminModals/policyModal";
import CustomSelect from "../../../components/common/customSelect";
import { showToast } from "../../../components/common/toastNotification";
import DeleteModal from "../../../components/common/DeleteModal";

const PolicyDashboard = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPageCount, setTotalPageCount] = useState(10);
  const [country, setCountry] = useState("India");
  const [tableData, setTableData] = useState<TablePolicyFormData[]>([]);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyUpdateData, setPolicyUpdateData] = useState<TablePolicyFormData>(
    {}
  );
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleCountryOnChange = (
    e: SelectChangeEvent<string | number> | string
  ) => {
    const next = typeof e === "string" ? e : String(e.target.value);
    setCountry(next);
    setPage(0);
  };
  const fetchTableData = useCallback(async () => {
    const input: getPolicyDashboardData = {
      pageNo: page + 1,
      pageSize,
      country: country,
    };

    try {
      const response = await adminServices.getPolicyDashboardData(input);
      setTableData(response.data.data.data);
      setTotalPageCount(response.data.data.totalRecord);
    } catch (error) {
      console.error("Failed to fetch table data", error);
      setTableData([]);
    }
  }, [page, pageSize, country]);
  const handlePolicyModalOpen = () => {
    setPolicyModalOpen(true);
    setPolicyUpdateData({});
  };
  const handleDeleteData = async () => {
    if (deleteId === null) return;

    try {
      const response = await adminServices.deletePolicyAPI(deleteId);
      if (response?.data?.success) {
        showToast("Policy deleted successfully", "success");
        fetchTableData();
      } else {
        showToast(response?.data?.message || "Failed to delete", "error");
      }
    } catch (err) {
      showToast("API error while deleting", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  const handlePolicyUpdateData = (data: TablePolicyFormData) => {
    setPolicyUpdateData(data);
    setPolicyModalOpen(true);
  };
  useEffect(() => {
    fetchTableData();
  }, [page, pageSize, country, fetchTableData]);

  return (
    <>
      <Box p={3}>
        {/* Table and Filter */}
        <Card
          sx={{
            backgroundColor: "#212121",
            mb: 2,
            borderRadius: 0,
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Grid
              container
              style={{
                marginBottom: 8,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                Policies & Procedures
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "space-between" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Box sx={{ minWidth: { xs: 140, sm: 140 } }}>
                  <CustomSelect
                    name="country"
                    //label="Country"
                    backgroundColor="transparent"
                    placeHolder="Select Country"
                    value={country || ""}
                    onChange={handleCountryOnChange}
                    options={[
                      { label: "India", value: "India" },
                      { label: "UK", value: "UK" },
                    ]}
                  />
                </Box>
                <Button
                  className="downloadBtn f-14"
                  sx={{
                    borderRadius: 0,
                    background: "#212121",
                    minWidth: { xs: 140, sm: 140 },
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "0 15px",
                    marginLeft: { xs: 0, sm: 2 },
                  }}
                  onClick={handlePolicyModalOpen}
                >
                  <Typography>Add</Typography>
                  <img src={AddIcon} alt="filter" />
                </Button>
              </Box>
            </Grid>
            {/* Table Section */}
            <TableContainer
              sx={{
                backgroundColor: "#212121",
                padding: 0,
                minHeight: "300px",
              }}
            >
              <Table sx={{ overflow: "scroll" }}>
                <TableHead>
                  <TableRow>
                    {[
                      "Country",
                      "Policy Type",
                      "Document Name",
                      "URL",
                      "Action",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: "#fff",
                          background: "#0A0A0A",
                          fontWeight: "600",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        sx={{
                          color: "#fff",
                          textAlign: "center",
                          alignItems: "center",
                        }}
                      >
                        No data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: "#fff", minWidth: 100 }}>
                          {row.country}
                        </TableCell>

                        <TableCell sx={{ color: "#fff", minWidth: 100 }}>
                          {row.policyType}
                        </TableCell>

                        <TableCell sx={{ color: "#fff", minWidth: 150 }}>
                          {row.documentName}
                        </TableCell>

                        <TableCell sx={{ color: "#fff", minWidth: 200 }}>
                          {row.docUrl}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", minWidth: 80 }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <img
                              src={EditIcon}
                              alt="Edit"
                              style={{ cursor: "pointer" }}
                              width={20}
                              height={20}
                              onClick={(e) => {
                                e.preventDefault();
                                handlePolicyUpdateData(row);
                              }}
                            />

                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              style={{
                                marginLeft: "8px",
                                cursor: "pointer",
                              }}
                              width={20}
                              height={20}
                              onClick={() => {
                                setDeleteId(row.id ?? null);
                                setIsDeleteModalOpen(true);
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Grid
              style={{
                justifyContent: "flex-end",
                alignItems: "center",
                display: "flex",
                marginTop: 16,
                padding: 8,
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={totalPageCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  color: "#ffffff",
                  "& .MuiSelect-icon": { color: "#ffffff" },
                  "& .MuiSvgIcon-root": { color: "#ffffff" },
                }}
              />
            </Grid>
          </CardContent>
        </Card>
      </Box>
      <PolicyModal
        open={policyModalOpen}
        onClose={() => setPolicyModalOpen(false)}
        onSave={fetchTableData}
        policyUpdateData={policyUpdateData}
      />
      <DeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteId(null);
        }}
        onDelete={handleDeleteData}
      />
    </>
  );
};

export default PolicyDashboard;
